import {
  FETCH_DATA_REQUEST,
  FETCH_DATA_SUCCESS,
  FETCH_DATA_FAILURE,
} from "./actionTypes";
import API_ENDPOINTS from "../api/apiEndpoint";
import { axiosBaseQuery, buildUrl, getServiceType } from "../api/axiosBaseQuery";
import { ENCRYPTION } from "../constant/globalConstants";
import { encrypted, tokenManager } from "../utils/utility";

/**
 * Core fetch action using axiosBaseQuery
 */
export const fetchData = (
  type,
  body = null,
  queryParams = null,
  method = "GET",
  options = {}
) => {
  return async (dispatch) => {
    let endpoint = API_ENDPOINTS[type];
    if (!endpoint) {
      return dispatch({
        type: FETCH_DATA_FAILURE,
        api: type,
        payload: "Invalid API type",
      });
    }

    if (options.urlParams) {
      Object.entries(options.urlParams).forEach(([key, value]) => {
        endpoint = endpoint.replace(`{${key}}`, value);
      });
    }

    dispatch({ type: FETCH_DATA_REQUEST, api: type });

    try {
      const result = await axiosBaseQuery({
        url: endpoint,
        method,
        body,
        params: queryParams,
        options,
      });

      if (result.error) {
        dispatch({
          type: FETCH_DATA_FAILURE,
          api: type,
          payload: result.error.message,
        });
        return result;
      }

      // Success
      let payloadToStore = result.data;
      if (options.responseType === 'blob' || options.responseType === 'arraybuffer') {
        payloadToStore = "Binary data received";
      }
      dispatch({ type: FETCH_DATA_SUCCESS, api: type, payload: payloadToStore });
      return result.data;
    } catch (error) {
      dispatch({
        type: FETCH_DATA_FAILURE,
        api: type,
        payload: error.message,
      });
      return { data: null, error };
    }
  };
};

// GET action creator
export const createGetAction = (apiType, options = {}) => {
  return (queryParams = null) => (dispatch) => {
    return dispatch(fetchData(apiType, null, queryParams, "GET", options));
  };
};

// POST action creator
export const createPostAction = (apiType, options = {}) => {
  return (body = null, queryParams = null) => {
    return (dispatch) => {
      return dispatch(fetchData(apiType, body, queryParams, "POST", options));
    };
  };
};

/**
 * Core streaming fetch action
 */
export const fetchStreamData = (apiType, payload, options, abortController) => {
  let endpoint = API_ENDPOINTS[apiType];

  if (options && options.urlParams) {
    Object.entries(options.urlParams).forEach(([key, value]) => {
      endpoint = endpoint.replace(`{${key}}`, value);
    });
  }

  const serviceType = getServiceType(options);
  const fullUrl = buildUrl(endpoint, serviceType);
  const token = tokenManager.getToken("token");

  const body = ENCRYPTION
    ? JSON.stringify({
      encryptedBody: encrypted.encrypt(JSON.stringify(payload)),
    })
    : JSON.stringify(payload);

  return fetch(fullUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body,
    signal: abortController?.signal,
  });

};

// ============================================
// API Actions
// ============================================

// Auth & RBAC - Middleware
export const fetchAuthInfo = (queryParams = null) => {
  return createGetAction("authInfo", { skipAuth: true })(queryParams);
};

export const authenticateUser = (body = null) => {
  return createPostAction("authLogin", { skipAuth: true })(body);
};

export const triggerLogout = (queryParams = null) => {
  return createGetAction("authLogout")(queryParams);
};

export const fetchCurrentUser = (queryParams = null) => {
  return createGetAction("currentUser")(queryParams);
};

export const acceptMainPolicy = (data) => {
  return createPostAction("acceptPolicy")(data);
};

export const authenticateDOCRequest = (data) => {
  return createPostAction("authenticateDOCPolicy")(data);
};

export const acceptDOCPolicy = (data) => {
  return createPostAction("acceptDOCPolicy")(data);
};

// userManagement
export const fetchAuthorizeApps = (queryParams = null) => {
  return createGetAction("authorizeApps")(queryParams);
};

export const fetchAuthorizeRoles = (queryParams = null) => {
  return createGetAction("authorizeRoles")(queryParams);
};

export const createAppUser = (data) => {
  return createPostAction("appUsers")(data);
};

export const createAuthorizeMapping = (data) => {
  return createPostAction("authorizeMapping")(data);
};

// Chat Genie AI Chat
export const fetchRbacAuthorization = (queryParams = null) => {
  return createGetAction("rbacAuthorization", { useAIChatURL: true })(queryParams);
};

export const fetchModelList = (queryParams = null) => {
  return createGetAction("getModelList", { useAIChatURL: true })(queryParams);
};

export const uploadFileData = (file, cancelTokenSource = null) => {
  return createPostAction("uploadFile", cancelTokenSource ? { useAIChatURL: true, cancelTokenSource } : { useAIChatURL: true })(file);
};

export const fetchHRBotCompletions = (data) => {
  return createPostAction("hrBotCompletions", { useAIChatURL: true })(data);
};

export const fetchFileBytesData = (queryParams) => {
  return createGetAction("fileData", { useAIChatURL: true })(queryParams);
};

// AI Chat Threads and Conversations
export const fetchDocumentCitationChunks = (data) => {
  return createPostAction("getDocumentCitations", { useAIChatURL: true })(data);
};

export const getUserThreadsConversation = (queryParams) => {
  return createGetAction("getUserThreads", { useAIChatURL: true })(queryParams);
};

export const fetchUserConversationRequest = (queryParams) => {
  return createGetAction("getUserConversation", { useAIChatURL: true })(queryParams);
};

export const updateUserThreadRequest = (data) => {
  return createPostAction("updateUserThread", { useAIChatURL: true })(data);
};

export const updateChatFeedbackRequest = (data) => {
  return createPostAction("updateChatFeedback", { useAIChatURL: true })(data);
};

export const fetchEnhanceQueryRequest = (data) => {
  return createPostAction("fetchEnhanceQuery", { useAIChatURL: true })(data);
};

export const updateProductivityFeedbackRequest = (data) => {
  return createPostAction("updateProductivityFeedback", { useAIChatURL: true })(data);
};

export const updateWebSearchConsent = (data) => {
  return createPostAction("updateWebSearchConsent", { useAIChatURL: true })(data);
};

export const fetchNpsEligibility = (queryParams) => {
  return createGetAction("getNpsEligibility", { useAIChatURL: true })(queryParams);
};

export const fetchPromptsByType = (queryParams) => {
  return createGetAction("prompts", { useAIChatURL: true })(queryParams);
};

export const managePrompts = (data) => {
  return createPostAction("prompts", { useAIChatURL: true })(data);
};

export const fetchImageToolEnhancePrompt = (data) => {
  return createPostAction("imageToolEnhancePrompt", { useAIChatURL: true })(data);
};

/**
 * Streaming action creator for chat completions
 * Returns a fetch promise with streaming support
 */
export const fetchChatCompletions = (payload, abortController) => {
  return fetchStreamData("chatCompletions", payload, { useAIChatURL: true }, abortController);
};

// Product Label
export const fetchProductLabelAIChatCompletions = (payload, abortController) => {
  return fetchStreamData("productLabelAIChatCompletions", payload, { useProductLabelAIURL: true }, abortController);
};

export const fetchProductLabelFileBytesData = (queryParams) => {
  return createGetAction("fetchProductLabelFileData", { useProductLabelAIURL: true, responseType: 'blob' })(queryParams);
};

// AI CHAT Excel
export const handleChatExcelFileUpload = (file, cancelTokenSource = null) => {
  return createPostAction("chatExcelFileUpload", cancelTokenSource ? { useAIChatExcelURL: true, cancelTokenSource } : { useAIChatExcelURL: true })(file);
};

export const fetchChatExcelCompletions = (payload, abortController) => {
  return fetchStreamData("chatExcelCompletions", payload, { useAIChatExcelURL: true }, abortController);
};

// Translations
export const translateText = (data) => {
  return createPostAction("translationText", { useTranslateURL: true })(data);
};

export const translateDocs = (data) => {
  return createPostAction("translationDocs", { useTranslateURL: true })(data);
};

export const translateFeedbackStatus = (data) => {
  return createPostAction("translationFeedback", { useTranslateURL: true })(data);
};

export const fetchTranslateHistory = (data) => {
  return createPostAction("translateHistory", { useTranslateURL: true })(data);
};

export const translateFileStatus = (queryParams = null) => {
  return createGetAction("getTranslationFileStatus", { useTranslateURL: true })(queryParams);
};

export const fetchTranslatedFile = (queryParams = null) => {
  return createGetAction("getTranslatedFile", { useTranslateURL: true })(queryParams);
};

export const fetchTranslationLanguages = (queryParams = null) => {
  return createGetAction("getTranslationLanguage", { useTranslateURL: true })(queryParams);
};


// Procurement
export const createProcurementSession = (data = {}) => {
  return createPostAction("procurementCreateSession", { useProcurementURL: true })(data);
};

export const getProcurementProgressTracker = (queryParams = null) => {
  return createGetAction("procurementProgressTracker", { useProcurementURL: true })(queryParams);
};

export const getProcurementChartStatus = (queryParams = null) => {
  return createGetAction("procurementChartStatus", { useProcurementURL: true })(queryParams);
};

export const fetchProcurementChatCompletions = (data, cancelTokenSource = null) => {
  return createPostAction("procurementChatCompletions", {
    useProcurementURL: true,
    ...(cancelTokenSource ? { cancelTokenSource } : {})
  })(data);
};

export const updateProcurementFeedback = (data) => {
  return createPostAction("procurementFeedback", { useProcurementURL: true })(data);
};

export const getUserProcurementThreadsConversation = (queryParams) => {
  return createGetAction("getProcurementHistoryList", { useProcurementURL: true })(queryParams);
};

export const deleteProcurementUserThreadRequest = (data) => {
  return createPostAction("deleteProcurementUserThread", { useProcurementURL: true })(data);
};

export const renameProcurementUserThreadRequest = (data) => {
  return createPostAction("renameProcurementUserThread", { useProcurementURL: true })(data);
};

export const fetchProcurementUserConversation = (queryParams) => {
  return createGetAction("getProcurementChatConversation", { useProcurementURL: true })(queryParams);
};

export const fetchProcurementTemplateRequest = (data, cancelTokenSource = null) => {
  return createPostAction("procurementTemplatePrompts", {
    useProcurementURL: true,
    ...(cancelTokenSource ? { cancelTokenSource } : {})
  })(data);
};

export const fetchProcurementTemplateOptions = (queryParams = null) => {
  return createGetAction("procurementTemplateOptions", { useProcurementURL: true })(queryParams);
};


// GMT

export const generateCustomGMTSessionId = (data) => {
  return createPostAction("GMTSessionId", { useGMTURL: true })(data);
};

export const fetchGMTChatCompletions = (data) => {
  return createPostAction("GMTChatCompletions", { useGMTURL: true })(data);
};

// GMT Threads
export const getUserGMTThreadsConversation = (queryParams) => {
  return createGetAction("getGMTHistoryList", { useGMTURL: true })(queryParams);
};

export const deleteGMTUserThreadRequest = (data) => {
  return createPostAction("deleteGMTUserThread", { useGMTURL: true })(data);
};

export const renameGMTUserThreadRequest = (data) => {
  return createPostAction("renameGMTUserThread", { useGMTURL: true })(data);
};

export const updateGMTFeedbackRequest = (data) => {
  return createPostAction("updateGMTFeedback", { useGMTURL: true })(data);
};

export const fetchGMTUserConversation = (queryParams) => {
  return createGetAction("getGMTChatConversation", { useGMTURL: true })(queryParams);
};

// Deep Insights
export const fetchDeepInsightsAlerts = (queryParams) => {
  return createGetAction("getDeepInsightsAlerts", { useDeepInsightURL: true })(queryParams);
};

export const fetchAllDeepInsightsAlerts = (queryParams) => {
  return createGetAction("getDeepInsightsAllAlerts", { useDeepInsightURL: true })(queryParams);
};

export const fetchDeepInsightsChatCompletions = (payload, abortController) => {
  return fetchStreamData("deepInsightsChatCompletions", payload, { useDeepInsightURL: true }, abortController);
};

export const regenerateDeepInsightsChatCompletions = (payload, abortController) => {
  return fetchStreamData("deepInsightsRegenerate", payload, { useDeepInsightURL: true }, abortController);
};

export const getDeepInsightsThreadsConversation = (queryParams) => {
  return createGetAction("deepInsightsSession", { useDeepInsightURL: true })(queryParams);
};

export const createDeepInsightsSession = (data) => {
  return createPostAction("deepInsightsSession", { useDeepInsightURL: true })(data);
};

export const updateDeepInsightsSessionRequest = (data) => {
  return createPostAction("updateDeepInsightsSession", { useDeepInsightURL: true })(data);
};

export const fetchDeepInsightsUserConversation = (queryParams) => {
  return createGetAction("getDeepInsightsUserConversation", { useDeepInsightURL: true })(queryParams);
};
export const postDeepInsightsFeedback = (data) => {
  return createPostAction("deepInsightsFeedback", { useDeepInsightURL: true })(data);
};

export const fetchDeepInsightsConnectors = () => {
  return createGetAction("getDeepInsightsConnectors", { useDeepInsightURL: true })();
};

export const runDeepInsightsPlaybook = (playbookId, data) => {
  return createPostAction("deepInsightsPlaybookRun", {
    useDeepInsightURL: true,
    urlParams: { playbook_id: playbookId },
  })(data);
};

// Talent Pulse
export const fetchTalentPulseCompletions = (payload, abortController) => {
  return fetchStreamData("talentPulseChatCompletions", payload, { useTalentPulseURL: true }, abortController);
};

export const getTalentPulseThreadsConversation = (queryParams) => {
  return createGetAction("getTalentPulseHistoryList", { useTalentPulseURL: true })(queryParams);
};

export const fetchTalentPulseUserConversation = (queryParams) => {
  return createGetAction("getTalentPulseUserConversation", { useTalentPulseURL: true })(queryParams);
};

export const updateTalentPulseUserThread = (data) => {
  return createPostAction("updateTalentPulseUserThread", { useTalentPulseURL: true })(data);
};

export const updateTalentPulseFeedback = (data) => {
  return createPostAction("talentPulseFeedback", { useTalentPulseURL: true })(data);
};

export const stopTalentPulseChatStream = (data) => {
  return createPostAction("talentPulseChatStop", { useTalentPulseURL: true })(data);
};

export const handleTalentPulseCitationDownload = (data) => {
  return createGetAction("talentPulseCitationDownload", { useTalentPulseURL: true, responseType: 'blob' })(data);
};

export const updateTalentPulseWebSearchConsent = (data) => {
  return createPostAction("talentPulseChatConsent", { useTalentPulseURL: true })(data);
};

export const fetchTalentPulseProfiles = (queryParams = null) =>
  createPostAction("searchTalentPulseProfiles", {
    useTalentPulseURL: true,
  })(queryParams);

export const fetchTalentPulseProfileFilters = (queryParams = null) =>
  createGetAction("getTalentPulseProfileFilters", { useTalentPulseURL: true })(queryParams);

export const fetchTalentPulseProfileDetail = (id) =>
  createGetAction("getTalentPulseProfileDetail", { useTalentPulseURL: true, urlParams: { id } })();

export const fetchTalentPulseSurveys = (queryParams = null) =>
  createGetAction("talentPulseSurveys", { useTalentPulseURL: true })(queryParams);

export const fetchTalentPulseSurveySends = (queryParams = null) =>
  createGetAction("talentPulseSurveySends", { useTalentPulseURL: true })(queryParams);

export const createTalentPulseSurvey = (data) =>
  createPostAction("talentPulseSurveys", { useTalentPulseURL: true })(data);

export const sendTalentPulseSurvey = (data) =>
  createPostAction("talentPulseSurveySend", { useTalentPulseURL: true })(data);

export const fetchSurveyUsers = (queryParams = null) =>
  createGetAction("talentPulseSurveyUsers", { useTalentPulseURL: true })(queryParams);


// talent pulse admin
export const fetchTalentPulseAdminOptions = (queryParams = null) =>
  createGetAction("talentPulseAdminOptions", { useTalentPulseURL: true })(queryParams);

export const fetchTalentPulseAdminFiles = (queryParams = null) =>
  createGetAction("talentPulseAdminFiles", { useTalentPulseURL: true })(queryParams);

export const uploadTalentPulseAdminFile = (data, cancelTokenSource = null) =>
  createPostAction("talentPulseAdminFileUpload", { useTalentPulseURL: true, ...(cancelTokenSource && { cancelTokenSource }) })(data);

export const downloadTalentPulseAdmin = (data) => (dispatch) =>
  dispatch(fetchData("talentPulseAdminDownload", data, null, "POST", { useTalentPulseURL: true, responseType: "blob" }));


// Talent Pulse Survey
export const fetchTalentPulseSurveyStatus = (surveySendId, queryParams = null) =>
  createGetAction("talentPulseSurveyStatus", { useTalentPulseURL: true, urlParams: { survey_send_id: surveySendId, survey_id: surveySendId } })(queryParams);

export const analyseTalentPulseSurvey = (surveySendId, data = {}) =>
  createPostAction("talentPulseSurveyAnalyse", { useTalentPulseURL: true, urlParams: { survey_send_id: surveySendId, survey_id: surveySendId } })(data);

export const fetchTalentPulseSurveyDashboard = (surveySendId, queryParams = null) =>
  createGetAction("talentPulseSurveyDashboard", { useTalentPulseURL: true, urlParams: { survey_send_id: surveySendId, survey_id: surveySendId } })(queryParams);

export const fetchTalentPulseSurveyCardsData = (surveySendId, surveyType, queryParams = null) =>
  createGetAction("talentPulseSurveyCardsData", { useTalentPulseURL: true, urlParams: { survey_send_id: surveySendId, survey_id: surveySendId, survey_type: surveyType } })(queryParams);

export const fetchTalentPulseSurveyAction = (surveySendId, queryParams = null) =>
  createGetAction("talentPulseSurveyAction", { useTalentPulseURL: true, urlParams: { survey_send_id: surveySendId, survey_id: surveySendId } })(queryParams);


/**
 * Returns the fully resolved WebSocket URL for Speech endpoints.
 * @param {string} type - "transcribe" for STT, "tts" for Azure VOP Text-to-Speech
 * @param {string} appType - "talentPulse" or "deepInsights"
 * @param {Object} queryParams - optional query params appended to the WS URL (e.g. { speed: 1.2 })
 * Converts the HTTP(S) base URL → WS(S) and appends the endpoint path.
 *
 * NOTE: queryParams is currently used by DeepInsights "tap-to-talk" overlay to set TTS speed.
 * It's optional + backward-compatible: existing call sites that pass only (type, appType) behave unchanged.
 */
export const getSpeechWsUrl = (type = "transcribe", appType = "talentPulse", queryParams = {}) => {
  let endpoint;
  let serviceOptions = {};

  if (appType === "deepInsights") {
    endpoint = type === "tts"
      ? API_ENDPOINTS.deepInsightsSpeechTTS
      : API_ENDPOINTS.deepInsightsSpeechTranscribe;
    serviceOptions = { useDeepInsightURL: true };
  } else {
    endpoint = type === "tts"
      ? API_ENDPOINTS.talentPulseSpeechTTS
      : API_ENDPOINTS.talentPulseSpeechTranscribe;
    serviceOptions = { useTalentPulseURL: true };
  }

  const httpUrl = buildUrl(endpoint, getServiceType(serviceOptions));
  const wsUrl = httpUrl
    .replace(/^https:\/\//, "wss://")
    .replace(/^http:\/\//, "ws://");

  if (queryParams && typeof queryParams === "object") {
    const qs = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      qs.set(key, String(value));
    });
    const qsStr = qs.toString();
    if (qsStr) {
      return `${wsUrl}${wsUrl.includes("?") ? "&" : "?"}${qsStr}`;
    }
  }

  return wsUrl;
};
